/*=== CORE IMPORTS ===*/
import { html } from 'lit';
import { map } from 'lit/directives/map.js';
import { ViewBase } from '../view-base.js';
import { homeSignedInViewStyles } from './home-signed-in-view-styles.js';

/*=== LIBRARY COMPONENTS ===*/
import '@shoelace-style/shoelace/dist/components/animation/animation.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/relative-time/relative-time.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';

/*=== Q1 COMPONENTS ===*/
import '../../components/q1-guild-chip/q1-guild-chip.js';
import '../../components/q1-project-chip/q1-project-chip.js';

/*=== Q1 MODULES ===*/
import { buildRefPathMap } from '../../modules/cache-helpers-client.js';
import { enrichGuild } from '../../modules/guild-helpers-client.js';
import { enrichProject } from '../../modules/project-helpers-client.js';

/*=== CONSTANTS ===*/

const firstGreeting = 'Welcome to the q1 network!';

const allGreetings = [
  "Welcome back to the network!",
  "Howdy! It's great to see you again.",
  "Glad to have you with us today!",
  "Welcome! Let's get things moving.",
  "Welcome back! Let's make it count.",
  "Good to see you back in action!",
  "Hello! Let's dive in.",
  "Welcome! Let's create something amazing.",
  "Greetings! Your network awaits.",
  "It's wonderful to have you back.",
  "Hello again! Time for some work joy.",
  "It's great to have you here!",
  "Welcome back! Let's make progress.",
  "Good to see you! Let's get started.",
  "Welcome! Let's make great things happen.",
  "Hi! Let's connect and collaborate.",
];

const CONTRIBUTOR_UPGRADE_IN_PROGRESS_FLAG = 'contributor-upgrade-in-progress';

const CONTRIBUTOR_UPGRADE_WELCOME_FLAG = 'contributor-upgrade-welcome';

const CACHE_MAX_AGE = 1; // in minutes

const CACHES = (currentMemberId) => [
  'all-guilds-search',
  'all-projects-search',
  `member-inbox:${currentMemberId}`,
];

/*=== VIEW CLASS ===*/

export class HomeSignedInView extends ViewBase {
  static styles = [
    ...super.styles,
    homeSignedInViewStyles,
  ];

  static properties = {
    ...super.properties,
    
    greeting: { type: String },
    refreshing: { type: Boolean },
    refreshedAt: { type: Object },

    /** Stores ref path map of guilds and projects. */
    _refPathMap: { type: Object },

    /** Stores current member's inbox cache. */
    _inboxCache: { type: Object },
  };

  constructor() {
    super();

    this.greeting = firstGreeting;
    this.refreshing = false;
    this.refreshedAt = null;

    this._refPathMap = {};
    this._inboxCache = null;
  }

  /*=== LIFECYCLE ===*/

  connectedCallback() {
    super.connectedCallback();

    this.setViewMeta();

    this.initView();
  }

  initView() {
    this.setupDayAndGreeting();
    this.refreshCaches();

    this.updateComplete.then(() => {
      if (this.currentMember?.id) this.setAfLink_db('members', this.currentMember.id);
    });
  }

  /*=== ACTIONS ===*/

  async refreshCaches() {
    // Get the caches and immediately make them available for rendering
    // This all runs immediately so we don't even need to set refreshing to true yet.
    let allGuildsSearchCache, allProjectsSearchCache, inboxCache;
    let { caches, anyAreNewOrStale, refreshesComplete } = await this.appElement.cacheManager
      .getCaches(CACHES(this.currentMember.id), CACHE_MAX_AGE);
    [allGuildsSearchCache, allProjectsSearchCache, inboxCache] = caches;
    this._refPathMap = {
      ...buildRefPathMap(allGuildsSearchCache?.data, 'guilds', 'guilds'),
      ...buildRefPathMap(allProjectsSearchCache?.data, 'projects', 'projects'),
    };
    this._inboxCache = inboxCache;
    this.refreshedAt = new Date();

    // If any caches are new or stale, wait for refresh and re-render
    if (anyAreNewOrStale) {
      this.refreshing = true;
      [allGuildsSearchCache, allProjectsSearchCache] = await refreshesComplete;
      this._refPathMap = {
        ...buildRefPathMap(allGuildsSearchCache?.data, 'guilds', 'guilds'),
        ...buildRefPathMap(allProjectsSearchCache?.data, 'projects', 'projects'),
      };
      this.refreshing = false;
      this._inboxCache = inboxCache;
      this.refreshedAt = new Date();
    }
  }

  /*=== GETTERS ===*/
  
  get appHeaderVariant() {
    return 'white-text';
  }

  get pendingInboxCount() {
    return Object.keys(this._inboxCache?.data?.approverVersions ||{}).length;
  }

  get watchedInboxCount() {
    return Object.keys(this._inboxCache?.data?.proposerVersions ||{}).length;
  }

  get yourGuilds() {
    if (!Object.keys(this._refPathMap || {}).length) return [];
    if (!this.currentMember?.guildsActive?.length) return [];

    return this.currentMember.guildsActive
      .map((guildId) => enrichGuild(this._refPathMap[`guilds/${guildId}`]))
      .filter((guild) => guild);
  }

  get yourProjects() {
    if (!Object.keys(this._refPathMap || {}).length) return [];
    if (!this.currentMember?.projectsActive?.length) return [];

    return this.currentMember.projectsActive
      .map((projectId) => enrichProject(this._refPathMap[`projects/${projectId}`]))
      .filter((project) => project);
  }
  
  /*=== TEMPLATES ===*/

  get contentTemplate() {
    return html`
      <header-bar class="with-border">
        <h1>${this.greeting}</h1>
        
        <button-bar class="align-center sl-secondary">
          ${!this.refreshedAt ? null : html`
            <span class="timestamp">
              Refreshed
              <sl-relative-time sync .date=${this.refreshedAt}></sl-relative-time>
            </span>
          `}

          <sl-button
            id="refresh-button"
            @click=${this.refreshCaches}
            ?disabled=${this.refreshing}
          >
            ${this.refreshing ? html`
              <sl-spinner size="small" slot="prefix"></sl-spinner>
              Refreshing...
            ` : html`
              <sl-icon slot="prefix" library="material" name="refresh"></sl-icon>
              Refresh
            `}
          </sl-button>
        </button-bar>
      </header-bar>

      ${this.notificationBarTemplate}

      ${this.yourMembershipsTemplate}
      
      ${this.yourInboxTemplate}

      ${this.infoCardsTemplate}
    `;
  }

  get notificationBarTemplate() {
    if (this.currentMember?.flags?.includes(CONTRIBUTOR_UPGRADE_IN_PROGRESS_FLAG)) return html`
      <notice-bar class="primary">
        <span class="content">
          Contributor upgrade in progress...
        </span>

        <sl-button
          variant="text"
          href="/m/contributor-upgrade"
        >
          Continue Upgrade
          <sl-icon slot="suffix" library="material" name="arrow_circle_right"></sl-icon>
        </sl-button>
      </notice-bar>
    `;
    
    if (this.currentMember?.flags?.includes(CONTRIBUTOR_UPGRADE_WELCOME_FLAG)) return html`
      <sl-animation name="pulse" easing="ease" duration="1500" play>
        <notice-bar class="primary">
          <span class="content">
            Welcome to contributor membership! Finish your orientation...
          </span>

          <sl-button
            variant="text"
            href="/m/contributor-upgrade"
          >
            Complete Orientation
            <sl-icon slot="suffix" library="material" name="arrow_circle_right"></sl-icon>
          </sl-button>
        </notice-bar>
      </sl-animation>
    `;
  }

  get yourMembershipsTemplate() {
    return html`
      <section id="your-memberships">
        <div class="subsection">
          <div class="header"><h2>Your Guilds:</h2></div>

          ${!this.yourGuilds.length ? html`
            <div class="item empty">
              <sl-icon library="material" name="block"></sl-icon>
              You aren't a member of any guilds yet
            </div>
          ` : map(this.yourGuilds, (guild) => html`
            <div class="item">
              <q1-guild-chip .guild=${guild}></q1-guild-chip>
            </div>
          `)}
        </div>

        <div class="subsection">
          <div class="header"><h2>Your Projects:</h2></div>

          ${!this.yourProjects.length ? html`
            <div class="item empty">
              <sl-icon library="material" name="block"></sl-icon>
              You aren't a member of any projects yet
            </div>
          ` : map(this.yourProjects, (project) => html`
            <div class="item">
              <q1-project-chip .project=${project}></q1-project-chip>
            </div>
          `)}
        </div>
      </section>
    `;
  }

  get yourInboxTemplate() {
    return html`
      <sl-divider></sl-divider>
      
      <h2>Your Inbox</h2>

      <section id="your-inbox">
        <div class="item">
          <sl-icon library="material" name="help"></sl-icon>
          <span class="count">${this.pendingInboxCount}</span>
          <span class="label">pending actions</span>
        </div>

        <div class="item">
          <sl-icon library="material" name="visibility"></sl-icon>
          <span class="count">${this.watchedInboxCount}</span>
          <span class="label">watched actions</span>
        </div>
        
        <div class="item">
          <sl-button
            size="small"
            variant="warning"
            href="/inbox"
          >
            <sl-icon library="material" name="assignment"></sl-icon>
            View Inbox
          </sl-button>
        </div>
      </section>
    `;
  }

  get infoCardsTemplate() {
    return html`
      <sl-divider></sl-divider>

      <h2>Network Resources</h2>

      <section id="info-cards">
        <!-- <div class="item">
          <h3>q1 Network Slack</h3>

          <p>
            Join the q1 network slack to connect with other members and get help.
          </p>

          <sl-button
            variant="warning"
            href="https://join.slack.com/t/q1-is/shared_invite/zt-2xrcbblwj-3SvWUFFNk_NwlUo9KFB8Ug"
            target="_blank"
          >
            <sl-icon library="tabler" name="brand-slack" slot="prefix"></sl-icon>
            Join Slack
          </sl-button>
        </div> -->

        <div class="item">
          <h3>q1 Network Tutorials</h3>

          <p>
            Check out the tutorials for help orienting and understanding the network.
          </p>

          <sl-button
            variant="warning"
            href="/tutorials"
            target="_blank"
          >
            <sl-icon library="material" name="library_books" slot="prefix"></sl-icon>
            View Tutorials
          </sl-button>
        </div>

        <div class="item">
          <h3>Luma Calendar</h3>

          <p>
            Follow the q1 Network event calendar to participate in events and learning sessions.
          </p>

          <sl-button
            variant="warning"
            href="https://lu.ma/q1-network"
            target="_blank"
          >
            <sl-icon library="material" name="calendar_month" slot="prefix"></sl-icon>
            View Calendar
          </sl-button>
        </div>

        <div class="item">
          <h3>q1 Network Newsletter</h3>

          <p>
            Subscribe to the q1 network newsletter to get regular updates.
          </p>

          <sl-button
            variant="warning"
            href="http://eepurl.com/i9m11k"
            target="_blank"
          >
            <sl-icon library="material" name="alternate_email" slot="prefix"></sl-icon>
            Subscribe
          </sl-button>
        </div>
      </section>
    `;
  }

  /*=== UTILITY METHODS ===*/

  setupDayAndGreeting() {
    const today = this._dayAsNumber();
    let firstDay = JSON.parse(localStorage.getItem('firstDay'));

    // First check for the first day
    if (!firstDay) {
      firstDay = today;
      localStorage.setItem('firstDay', firstDay);
    }
    if (today === firstDay) return; // keep firstGreeting value

    // Then check for same day
    let lastDay = JSON.parse(localStorage.getItem('lastDay')) || today;
    let lastGreeting = localStorage.getItem('lastGreeting');
    if ((lastDay === today) && lastGreeting?.length) {
      this.greeting = lastGreeting;
      return;
    }

    // Else today is a new day! Time for a new greeting.
    lastDay = today;
    let remainingGreetings;
    try {
      remainingGreetings = JSON.parse(localStorage.getItem('remainingGreetings') || '[]');
    } catch (error) {
      console.log(`Error selecting random greeting: ${error.message}`);
    }
    if (!remainingGreetings?.length) remainingGreetings = [...allGreetings];

    const randomIndex = Math.floor(Math.random() * remainingGreetings.length);
    lastGreeting = remainingGreetings.splice(randomIndex, 1)[0];
    this.greeting = lastGreeting;

    // Now update local storage
    localStorage.setItem('lastDay', lastDay);
    localStorage.setItem('lastGreeting', lastGreeting);
    localStorage.setItem('remainingGreetings', JSON.stringify(remainingGreetings));
  }

  _dayAsNumber() {
    return Math.floor(Date.now() / (1000 * 60 * 60 * 24));
  }

}

customElements.define('home-signed-in-view', HomeSignedInView);
